import { Button, Drawer, Form, Input, Select, Space } from "antd";
import { useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import SelectDropdownLgIcon from "../../../../../assets/icons/SelectDropdownLgIcon";
import { ModalComponentProps } from "../../../../../types";
import {
  formatPhoneNumber,
  formatSPayNumber,
  selectSearchOption,
} from "../../../../../utils/utils";
import { FinancialModalType } from "./FinancialInfo";

type Props = {
  handleAdd: (data: Record<string, string | number>) => void;
} & ModalComponentProps<any, FinancialModalType>;

export default function MFSAddModal({
  handleClose,
  type,
  data,
  handleAdd,
  ...rest
}: Props) {
  const [form] = Form.useForm();
  const accountType = Form.useWatch("type", form);

  const onFinish = (values: Record<string, string | number>) => {
    const { account_no: num, type: account_type } = values;
    const account_no =
      accountType === "spay"
        ? formatSPayNumber(String(num))
        : formatPhoneNumber(String(num));

    if (type === "MFS_EDIT") {
      handleAdd({
        account_no,
        type: account_type,
        id: data?.id,
      });
    } else {
      handleAdd({
        account_no,
        type: account_type,
        id: uuidv4(),
      });
    }
    handleClose();
  };

  useEffect(() => {
    if (type === "MFS_EDIT" && data) {
      form.setFieldsValue({
        type: data.type,
        account_no: data.account_no,
      });
    }
  }, [type, data, form]);

  return (
    <Drawer
      title={`${
        type === "MFS_EDIT" ? "Edit" : type === "MFS_ADD" ? "Add" : "View"
      } MFS Details`}
      placement="right"
      width={480}
      className="right_site_drawer"
      onClose={handleClose}
      {...rest}
    >
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        <Form.Item
          name="type"
          label="Account Type"
          className="mb-1"
          rules={[
            {
              required: true,
              message: "Please select account type",
            },
          ]}
        >
          <Select
            placeholder="Select"
            size="large"
            allowClear
            showSearch
            filterOption={selectSearchOption}
            options={mobile_bank_list}
            suffixIcon={<SelectDropdownLgIcon />}
            onChange={() => form.resetFields(["account_no"])}
          />
        </Form.Item>
        <Form.Item
          name="account_no"
          label="Account Number"
          className="mb-4"
          rules={[
            {
              required: true,
              message: "Please enter account number",
            },
            {
              validator: async (_: any, value: string) => {
                if (!value) {
                  return Promise.resolve();
                }
                // Accept with or without 88 or +88
                const phoneRegex =
                  accountType === "spay"
                    ? /^(88)?(01[3-9])[0-9]{8}$/
                    : accountType === "rocket"
                    ? /^(?:\+?88)?(01[3-9])[0-9]{8,9}$/
                    : /^(?:\+?88)?(01[3-9])[0-9]{8}$/;
                if (!phoneRegex.test(value)) {
                  return Promise.reject(
                    new Error(
                      `Please enter a valid ${
                        mobile_bank_list?.find(
                          (el) => el?.value === accountType
                        )?.label
                      } wallet number`
                    )
                  );
                }
              },
            },
          ]}
        >
          <Input size="large" placeholder="Ex: 12345" />
        </Form.Item>

        <Space className="w-full mt-2" size={12}>
          <Button
            onClick={handleClose}
            className="normal-dark-button modal-button"
            size="middle"
          >
            Cancel
          </Button>
          {(type === "MFS_ADD" || type === "MFS_EDIT") && (
            <Button
              className="modal-button"
              type="primary"
              htmlType="submit"
              size="middle"
              //   loading={isLoading || updatePortalDepartmentIsLoading}
            >
              {data ? "Update" : "Save"}
            </Button>
          )}
        </Space>
      </Form>
    </Drawer>
  );
}

export const mobile_bank_list = [
  { label: "sPay", value: "spay", logo: "/bank-logo/spay.png" },
  { label: "bKash", value: "bkash", logo: "/bank-logo/bKash-Logo.svg" },
  { label: "Nagad", value: "nagad", logo: "/bank-logo/Nagad-Logo.png" },
  { label: "Rocket", value: "rocket", logo: "/bank-logo/Rocket_ddbl.png" },
  // { label: "Upay", value: "upay", logo: "/bank-logo/Upay_logo.png" },
];
